import React, { useEffect, useState } from 'react';
import { Timeline, Spin, message, Card } from 'antd';
import { SmileOutlined } from '@ant-design/icons';
import { useGlobalContext } from '../../store/GlobalContext';
import { AuditEvent } from '../../types';
import { getFieldChanges } from './auditChanges';
import { fetchAuditTrail } from './auditevent.service';
import styled from 'styled-components';
import startCase from 'lodash/startCase';

const TimeLineCardTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 8px;
`;

const ChangeList = styled.ul`
  margin: 0;
  padding-left: 18px;
  overflow-wrap: anywhere;
`;

const ChangeNote = styled.p`
  margin: 0;
  color: rgba(0, 0, 0, 0.45);
  list-style: none;
`;

const MAX_FIELDS = 8;

const OPERATION_VERBS: Record<string, string> = {
  INSERT: 'created',
  CREATE: 'created',
  UPDATE: 'updated',
  DELETE: 'deleted',
};

interface AuditTrailProps {
  showTitle?: boolean;
}

const AuditTrail: React.FC<AuditTrailProps> = ({ showTitle = true }) => {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const { auth } = useGlobalContext();

  useEffect(() => {
    const loadAuditTrail = async () => {
      try {
        if (auth.accessToken) {
          const fetchedEvents = await fetchAuditTrail(auth.accessToken);
          const newestFirst = [...fetchedEvents].sort(
            (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
          );
          setEvents(newestFirst.slice(0, 5));
        }
      } catch (error) {
        console.error(error);
        message.error("Failed to load audit trail");
      } finally {
        setLoading(false);
      }
    };

    loadAuditTrail();
  }, [auth.accessToken]);

  const renderChanges = (event: AuditEvent) => {
    if (event.operation !== 'UPDATE') return null;
    const fieldChanges = getFieldChanges(event.changes);
    if (fieldChanges.length === 0) {
      return <ChangeNote>No field changes recorded.</ChangeNote>;
    }
    const shown = fieldChanges.slice(0, MAX_FIELDS);
    return (
      <ChangeList>
        {shown.map((change) => (
          <li key={change.key}>
            <strong>{change.label}:</strong> {change.before} → {change.after}
          </li>
        ))}
        {fieldChanges.length > shown.length && (
          <ChangeNote as="li">and {fieldChanges.length - shown.length} more</ChangeNote>
        )}
      </ChangeList>
    );
  };

  function hasUser(event: AuditEvent): event is AuditEvent & { user: { firstName: string; accessLevel: string } } {
    return event.user !== undefined && event.user !== null;
  }

  // Convert events to Timeline items
  const timelineItems = events.map((event) => {
    const color =
      event.severity === 'high'
        ? 'red'
        : event.severity === 'medium'
        ? 'orange'
        : 'green';
    const dot =
      ['CREATE', 'INSERT'].includes(event.operation) ? <SmileOutlined /> : undefined;
    const description = (
      <div>
        <p style={{ marginBottom: 4 }}>
          <strong>{hasUser(event) ? event.user.firstName : 'Unknown user'}</strong>{' '}
          {OPERATION_VERBS[event.operation] ?? event.operation.toLowerCase()}{' '}
          <strong>{startCase(event.entityName)}</strong>
          <br />
          <ChangeNote as="span">{new Date(event.timestamp).toLocaleString()}</ChangeNote>
        </p>
        {renderChanges(event)}
      </div>
    );

    return {
      color: color,
      dot: dot,
      children: description,
    };
  });

  return (
    <div>
      <Card title={showTitle ? 'Audit Trail' : undefined} bordered={false}>
        {loading ? (
          <Spin tip="Loading audit trail..." />
        ) : (
          <Timeline mode={"left"} items={timelineItems} />
        )}
      </Card>
    </div>
  );
};

export default AuditTrail;
